import { useMemo, useState } from "react";
import { Drawer, message } from "antd";
import styled from "styled-components";
import { countryTaxConfigAPI, type CountryTaxConfigRow, type PaymentRoute } from "@/api/countryTaxConfig";
import { AppAutoComplete, AppButton, AppSelect, FormField } from "@/shared/components";
import { getApiErrorCode, getApiErrorMessage } from "@/shared/utils/apiError";
import { phoneCountries } from "@/shared/utils/phoneCountries";
import { businessToday } from "../utils/businessDate";
import {
	EMPTY_COUNTRY_CONFIG_FORM,
	previewLine,
	rowInForce,
	toCountryConfigInput,
	validateCountryConfig,
	type CountryConfigErrors,
	type CountryConfigForm,
} from "../utils/countryConfig";
import { REGISTRATION_PATTERNS, registrationPatternFor } from "../utils/registrationPatterns";

interface Props {
	open: boolean;
	initialCountryCode?: string;
	rows: CountryTaxConfigRow[];
	onClose: () => void;
	onCreated: (row: CountryTaxConfigRow) => void;
}

interface RateChangeFormProps {
	initialCountryCode?: string;
	/** Country panel: the country is fixed to the panel's country. */
	lockCountry?: boolean;
	rows: CountryTaxConfigRow[];
	onCancel: () => void;
	onCreated: (row: CountryTaxConfigRow) => void;
}

const today = businessToday;
const ROUTE_OPTIONS: ReadonlyArray<{ value: PaymentRoute; label: string; hint: string }> = [
	{ value: "cpi_direct", label: "CPI direct", hint: "Payment Statement issued" },
	{ value: "local_partner", label: "Local partner", hint: "No statement; partner pays" },
];
const nameOf = (code: string) => phoneCountries.find((c) => c.iso === code)?.name ?? code;

/** Which field a backend validation code belongs to (the backend is the authority). */
const FIELD_OF_CODE: Record<string, keyof CountryConfigForm> = {
	COUNTRY_INVALID: "countryCode",
	RATE_RANGE: "effectiveRatePercent",
	BAND_RANGE: "bandPoints",
	RATE_OUT_OF_BAND: "effectiveRatePercent",
	VALID_FROM_IN_PAST: "validFrom",
	PARTNER_REQUIRED: "partnerName",
	PATTERN_INVALID: "registrationNumberPattern",
	NOTES_REQUIRED: "notes",
	OVERLAP: "validFrom",
};

/** Seeds the form from the row in force so a rate change only asks for what changes. */
const seedFrom = (countryCode: string, rows: CountryTaxConfigRow[]): CountryConfigForm => {
	const current = rowInForce(
		rows.filter((r) => r.countryCode === countryCode),
		today(),
	);
	const pct = (rate: number | null) => (rate === null ? "" : String(Math.round(rate * 10000) / 100));
	// Unconfigured country: start from the pre-seeded label and pattern for it (an admin can override both).
	const preset = registrationPatternFor(countryCode);
	return {
		...EMPTY_COUNTRY_CONFIG_FORM,
		countryCode,
		validFrom: today(),
		...(preset ? { registrationNumberLabel: preset.label, registrationNumberPattern: preset.pattern } : {}),
		...(current
			? {
					taxName: current.taxName,
					taxNameLong: current.taxNameLong,
					taxAuthorityName: current.taxAuthorityName,
					registrationNumberLabel: current.registrationNumberLabel,
					registrationNumberPattern: current.registrationNumberPattern ?? "",
					statutoryRatePercent: pct(current.statutoryRate),
					effectiveRatePercent: pct(current.effectiveRate),
					bandPoints: String(current.bandPoints),
					paymentRoute: current.paymentRoute,
					partnerName: current.partnerName ?? "",
				}
			: {}),
	};
};

/** The rate-change form: used standalone in a drawer (toolbar) and inline inside the country panel. Mount fresh per use. */
export const RateChangeForm = ({
	initialCountryCode,
	lockCountry = false,
	rows,
	onCancel,
	onCreated,
}: RateChangeFormProps) => {
	const [form, setForm] = useState<CountryConfigForm>(() => seedFrom(initialCountryCode ?? "", rows));
	const [errors, setErrors] = useState<CountryConfigErrors>({});
	const [saving, setSaving] = useState(false);
	const [sample, setSample] = useState("");

	const current = useMemo(
		() =>
			rowInForce(
				rows.filter((r) => r.countryCode === form.countryCode),
				today(),
			),
		[form.countryCode, rows],
	);
	// Every payment partner already configured for any country, so names stay consistent across rows.
	const partnerOptions = useMemo(
		() =>
			[...new Set(rows.map((r) => r.partnerName?.trim()).filter((n): n is string => Boolean(n)))]
				.sort((a, b) => a.localeCompare(b))
				.map((name) => ({ value: name, label: name })),
		[rows],
	);
	const patternOptions = useMemo(
		() =>
			REGISTRATION_PATTERNS.map((p) => ({
				key: p.countryCode,
				value: p.pattern,
				label: `${nameOf(p.countryCode)} · ${p.label} · ${p.description}`,
			})),
		[],
	);
	const countryOptions = useMemo(() => {
		const configured = new Set(rows.map((r) => r.countryCode));
		return phoneCountries
			.map((c) => ({ value: c.iso, label: configured.has(c.iso) ? `${c.name} (configured)` : c.name }))
			.sort(
				(a, b) =>
					Number(!a.label.includes("(configured)")) - Number(!b.label.includes("(configured)")) ||
					a.label.localeCompare(b.label),
			);
	}, [rows]);

	const set = <K extends keyof CountryConfigForm>(field: K, value: CountryConfigForm[K]) => {
		setErrors((e) => ({ ...e, [field]: undefined }));
		setForm((f) => ({ ...f, [field]: value }));
	};
	const changeCountry = (code: string) => {
		setErrors({});
		setForm(seedFrom(code, rows));
	};

	const patternTest = (() => {
		if (!form.registrationNumberPattern.trim() || !sample.trim()) return null;
		try {
			return new RegExp(form.registrationNumberPattern).test(sample.replace(/\s+/g, ""))
				? "matches"
				: "does not match";
		} catch {
			return "pattern invalid";
		}
	})();

	const submit = async () => {
		const found = validateCountryConfig(form, today());
		setErrors(found);
		if (Object.keys(found).length) return;
		setSaving(true);
		try {
			const created = await countryTaxConfigAPI.create(toCountryConfigInput(form));
			message.success(
				`${nameOf(created.countryCode)}: ${created.taxName} ${created.effectiveRate === null ? "no tax" : `${Math.round(created.effectiveRate * 10000) / 100}%`} from ${created.validFrom}.`,
			);
			onCreated(created);
		} catch (error) {
			const code = getApiErrorCode(error);
			const field = code ? FIELD_OF_CODE[code] : undefined;
			if (field) setErrors({ [field]: getApiErrorMessage(error, "Invalid value") });
			else message.error(getApiErrorMessage(error, "Unable to save the rate change"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<FormRoot>
			<Fields>
				<FormField label="Country" htmlFor="cfg-country" error={errors.countryCode} required>
					<Select
						id="cfg-country"
						disabled={lockCountry}
						showSearch
						optionFilterProp="label"
						value={form.countryCode || undefined}
						onChange={(value) => changeCountry(String(value))}
						options={countryOptions}
					/>
				</FormField>
				<Preview aria-live="polite">
					{form.countryCode
						? previewLine(
								{
									countryName: nameOf(form.countryCode),
									validFrom: form.validFrom,
									effectiveRatePercent: form.effectiveRatePercent,
									taxName: form.taxName,
								},
								current,
							)
						: "Choose a country to see what this change does."}
				</Preview>
				<Row>
					<FormField label="Tax name (short)" htmlFor="cfg-tax-name" error={errors.taxName} required>
						<Input
							id="cfg-tax-name"
							value={form.taxName}
							placeholder="GST"
							onChange={(e) => set("taxName", e.target.value)}
						/>
					</FormField>
					<FormField label="Tax name (long)" htmlFor="cfg-tax-name-long" error={errors.taxNameLong} required>
						<Input
							id="cfg-tax-name-long"
							value={form.taxNameLong}
							placeholder="Goods and Services Tax"
							onChange={(e) => set("taxNameLong", e.target.value)}
						/>
					</FormField>
				</Row>
				<FormField label="Tax authority" htmlFor="cfg-authority" error={errors.taxAuthorityName} required>
					<Input
						id="cfg-authority"
						value={form.taxAuthorityName}
						placeholder="Australian Taxation Office"
						onChange={(e) => set("taxAuthorityName", e.target.value)}
					/>
				</FormField>
				<Row>
					<FormField
						label="Registration number label"
						htmlFor="cfg-label"
						error={errors.registrationNumberLabel}
						required
					>
						<Input
							id="cfg-label"
							value={form.registrationNumberLabel}
							placeholder="ABN"
							onChange={(e) => set("registrationNumberLabel", e.target.value)}
						/>
					</FormField>
					<FormField
						label="Number pattern (regex)"
						htmlFor="cfg-pattern"
						error={errors.registrationNumberPattern}
					>
						<PatternPicker
							id="cfg-pattern"
							value={form.registrationNumberPattern}
							placeholder="Pick a country format or type a regex"
							options={patternOptions}
							filterOption={(input, option) =>
								String(option?.label ?? "")
									.toLowerCase()
									.includes(input.trim().toLowerCase()) ||
								String(option?.value ?? "").includes(input.trim())
							}
							onChange={(value) => set("registrationNumberPattern", String(value ?? ""))}
							onSelect={(value) => {
								const preset = REGISTRATION_PATTERNS.find((p) => p.pattern === value);
								if (preset && !form.registrationNumberLabel.trim())
									set("registrationNumberLabel", preset.label);
							}}
						/>
					</FormField>
				</Row>
				<FormField label="Test a value against the pattern" htmlFor="cfg-sample">
					<Row>
						<Input
							id="cfg-sample"
							value={sample}
							placeholder="12 345 678 901"
							onChange={(e) => setSample(e.target.value)}
						/>
						<TestResult $ok={patternTest === "matches"}>{patternTest ?? "—"}</TestResult>
					</Row>
				</FormField>
				<Row>
					<FormField label="Statutory rate (%)" htmlFor="cfg-statutory" error={errors.statutoryRatePercent}>
						<Input
							id="cfg-statutory"
							inputMode="decimal"
							value={form.statutoryRatePercent}
							placeholder="10"
							onChange={(e) => set("statutoryRatePercent", e.target.value)}
						/>
					</FormField>
					<FormField label="Effective rate (%)" htmlFor="cfg-effective" error={errors.effectiveRatePercent}>
						<Input
							id="cfg-effective"
							inputMode="decimal"
							value={form.effectiveRatePercent}
							placeholder="10"
							onChange={(e) => set("effectiveRatePercent", e.target.value)}
						/>
					</FormField>
					<FormField label="Band (± points)" htmlFor="cfg-band" error={errors.bandPoints} required>
						<Input
							id="cfg-band"
							inputMode="decimal"
							value={form.bandPoints}
							onChange={(e) => set("bandPoints", e.target.value)}
						/>
					</FormField>
				</Row>
				<Hint>
					Leave both rates empty when the country has no such tax. The band stops a typo becoming a rate on a
					legal document.
				</Hint>
				<FormField label="Payment route" labelId="cfg-route-label" error={errors.partnerName}>
					<RouteToggle role="radiogroup" aria-labelledby="cfg-route-label">
						{ROUTE_OPTIONS.map((option) => (
							<RouteOption
								key={option.value}
								type="button"
								role="radio"
								aria-checked={form.paymentRoute === option.value}
								$active={form.paymentRoute === option.value}
								onClick={() => set("paymentRoute", option.value)}
							>
								<span>{option.label}</span>
								<small>{option.hint}</small>
							</RouteOption>
						))}
					</RouteToggle>
				</FormField>
				{form.paymentRoute === "local_partner" ? (
					<FormField label="Partner name" htmlFor="cfg-partner" error={errors.partnerName} required>
						<PatternPicker
							id="cfg-partner"
							value={form.partnerName}
							placeholder="Pick a payment partner or type a new one"
							options={partnerOptions}
							filterOption={(input, option) =>
								String(option?.value ?? "")
									.toLowerCase()
									.includes(input.trim().toLowerCase())
							}
							onChange={(value) => set("partnerName", String(value ?? ""))}
						/>
					</FormField>
				) : null}
				<FormField label="Valid from" htmlFor="cfg-valid-from" error={errors.validFrom} required>
					<Input
						id="cfg-valid-from"
						type="date"
						min={today()}
						value={form.validFrom}
						onChange={(e) => set("validFrom", e.target.value)}
					/>
				</FormField>
				<FormField label="Reason for the change (audit note)" htmlFor="cfg-notes" error={errors.notes} required>
					<TextArea
						id="cfg-notes"
						rows={3}
						value={form.notes}
						placeholder="e.g. Rate rises to 10% on 1 Oct per the 2026 budget"
						onChange={(e) => set("notes", e.target.value)}
					/>
				</FormField>
			</Fields>
			<Footer>
				<FooterNote>
					No edit, no delete: a mistake is corrected by adding another row. Every change is audited.
				</FooterNote>
				<FooterActions>
					<AppButton variant="outline" onClick={onCancel} disabled={saving}>
						Cancel
					</AppButton>
					<AppButton variant="primary" onClick={() => void submit()} disabled={saving}>
						{saving ? "Saving…" : "Save rate change"}
					</AppButton>
				</FooterActions>
			</Footer>
		</FormRoot>
	);
};

/** Toolbar entry point: the form in its own drawer (any country). */
export const AddRateChangeDrawer = ({ open, initialCountryCode, rows, onClose, onCreated }: Props) => (
	<Drawer open={open} onClose={onClose} title="Add rate change" width={560} destroyOnClose>
		{open ? (
			<RateChangeForm
				initialCountryCode={initialCountryCode}
				rows={rows}
				onCancel={onClose}
				onCreated={onCreated}
			/>
		) : null}
	</Drawer>
);

const FormRoot = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;
const Fields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;
const Row = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 12px;
	align-items: end;
`;
const Input = styled.input`
	width: 100%;
	height: 40px;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	outline: none;
	background: #fff;
	color: #171717;
	font: inherit;
	font-size: 15px;
	&:focus {
		border-color: #9fadf1;
	}
`;
const TextArea = styled.textarea`
	width: 100%;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	outline: none;
	font: inherit;
	font-size: 15px;
	resize: vertical;
	&:focus {
		border-color: #9fadf1;
	}
`;
const PatternPicker = styled(AppAutoComplete)`
	width: 100%;
	.ant-select-selector {
		height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}
	.ant-select-selection-search-input {
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace !important;
	}
`;
const Select = styled(AppSelect)`
	width: 100%;
	height: 40px;
	.ant-select-selector {
		height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}
`;
const RouteToggle = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
`;
const RouteOption = styled.button<{ $active: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	padding: 10px 14px;
	border-radius: 8px;
	border: 1px solid ${({ $active }) => ($active ? "#3a0ca3" : "#cdd4f6")};
	background: ${({ $active }) => ($active ? "#f3f5ff" : "#fff")};
	color: #303030;
	text-align: left;
	cursor: pointer;
	font: inherit;
	span {
		font-size: 14px;
		font-weight: 600;
		color: ${({ $active }) => ($active ? "#3a0ca3" : "#303030")};
	}
	small {
		font-size: 12px;
		color: #707070;
	}
	&:hover,
	&:focus-visible {
		border-color: #3a0ca3;
		outline: none;
	}
`;
const Preview = styled.p`
	margin: 0;
	padding: 12px 16px;
	border-radius: 12px;
	background: #f3f5ff;
	border: 1px solid #cdd4f6;
	color: #303030;
	font-size: 14px;
	line-height: 1.5;
`;
const Hint = styled.p`
	margin: -8px 0 0;
	color: #707070;
	font-size: 12px;
`;
const TestResult = styled.span<{ $ok: boolean }>`
	align-self: center;
	color: ${({ $ok }) => ($ok ? "#377608" : "#8a5a00")};
	font-size: 13px;
	font-weight: 600;
`;
const Footer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding-top: 16px;
	border-top: 1px solid #ebeefd;
`;
const FooterNote = styled.p`
	margin: 0;
	color: #707070;
	font-size: 12px;
`;
const FooterActions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;
